import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CrudPage from "@/components/CrudPage";
import { DataTable, InfoGrid, StatusBadge, Panel } from "@/components/shared";
import { api, notifyErr, fmtDate, fmtDateTime } from "@/lib/api";
import { EMPLOYEE_FIELDS } from "@/lib/forms";
import { useAuth } from "@/context/AuthContext";

const COLS = [
  { key: "last_name", label: "Cognome", render: (r) => <span className="font-medium">{r.last_name}</span> }, { key: "first_name", label: "Nome" },
  { key: "role_title", label: "Ruolo" }, { key: "phone", label: "Telefono" }, { key: "email", label: "Email" }, { key: "start_date", label: "Inizio", type: "date" },
  { key: "active", label: "Stato", render: (r) => <StatusBadge text={r.active ? "Attivo" : "Non attivo"} tone={r.active ? "green" : "zinc"} /> },
  { key: "user_id", label: "Account", render: (r) => (r.user_id ? <StatusBadge text="Collegato" tone="blue" /> : "—") },
];

function LinkAccount({ emp, onDone }) {
  const [f, setF] = useState({ email: emp.email || "", password: "", role: "operator", name: `${emp.first_name} ${emp.last_name}` });
  const create = async () => {
    try { await api.post("/users", { ...f, employee_id: emp.id }); toast.success("Account creato e collegato al dipendente"); onDone(); } catch (e) { notifyErr(e); }
  };
  return (
    <div className="grid sm:grid-cols-4 gap-2 items-end" data-testid="link-account-form">
      <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="link-account-email" />
      <Input placeholder="Password (min 8)" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="link-account-password" />
      <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="operator">Operatore</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select>
      <Button className="btn-brand" onClick={create} data-testid="link-account-submit">Crea account</Button>
    </div>
  );
}

export default function Employees() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { state } = useAuth();
  const [emp, setEmp] = useState(null);
  const [act, setAct] = useState(null);
  const id = sp.get("id");
  const load = () => {
    if (!id) { setEmp(null); return; }
    api.get(`/employees/${id}`).then((r) => setEmp(r.data)).catch(notifyErr);
    api.get(`/employees/${id}/activity`).then((r) => setAct(r.data.map((x, i) => ({ ...x, id: `${x.id}-${i}`, rid: x.id })))).catch(notifyErr);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);
  return (
    <>
      <CrudPage title="Dipendenti" subtitle="Il dipendente non deve avere per forza un account gestionale" endpoint="/employees" module="employees" columns={COLS} fields={EMPLOYEE_FIELDS}
        newLabel="Nuovo dipendente" searchPlaceholder="Cerca per nome, cognome, ruolo…" rowLink={(r) => `/dipendenti?id=${r.id}`} initialForm={{ active: true }}
        filters={[{ key: "active", placeholder: "Stato", options: [["true", "Attivi"], ["false", "Non attivi"]] }]} />
      <Dialog open={!!emp} onOpenChange={(o) => !o && setSp({})}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="employee-detail">
          {emp && <>
            <DialogHeader><DialogTitle>{emp.first_name} {emp.last_name}</DialogTitle></DialogHeader>
            <InfoGrid items={[["Ruolo", emp.role_title], ["Telefono", emp.phone], ["Email", emp.email], ["Inizio", fmtDate(emp.start_date)], ["Fine", fmtDate(emp.end_date)],
              ["Stato", emp.active ? "Attivo" : "Non attivo"], ["Note operative", emp.notes]]} />
            <Panel title="Account gestionale">
              {emp.user_id ? <p className="text-sm text-zinc-300">Account collegato. Ruolo e permessi si gestiscono in Impostazioni › Utenti.</p>
                : state.user.role === "superadmin" ? <LinkAccount emp={emp} onDone={load} /> : <p className="text-sm text-zinc-500">Nessun account collegato.</p>}
            </Panel>
            <h3 className="font-display font-semibold">Storico attività</h3>
            <DataTable rows={act} testid="employee-activity-table" empty="Nessuna attività registrata"
              onRowClick={(r) => (r.type === "Noleggio" ? nav(`/noleggi/${r.rid}`) : r.type === "Prenotazione" ? nav(`/prenotazioni?id=${r.rid}`) : null)}
              columns={[{ key: "type", label: "Tipo" }, { key: "role", label: "Ruolo" }, { key: "code", label: "Riferimento", type: "mono" },
                { key: "date", label: "Data", render: (r) => fmtDateTime(r.date) }, { key: "status", label: "Stato", type: "status" }]} />
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
